// src/pages/AdminSellerForm.jsx
// 商家表單頁面 - 新增或編輯商家（管理者模式）

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save,
  ArrowLeft,
  Store,
  MapPin
} from 'lucide-react';

const AdminSellerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [loadingSeller, setLoadingSeller] = useState(isEditMode);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    rating: '0.0',
    response_rate: '95',
    response_time: '24小時內'
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      loadSeller();
    }
  }, [id]);

  const loadSeller = async () => {
    setLoadingSeller(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/admin/sellers/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const result = await response.json();

      if (result.error) {
        throw new Error(result.message);
      }

      const seller = result.data;
      setFormData({
        name: seller.name || '',
        location: seller.location || '',
        rating: seller.rating || '0.0',
        response_rate: seller.response_rate || '95',
        response_time: seller.response_time || '24小時內'
      });
    } catch (err) {
      console.error('載入商家失敗:', err);
      alert('載入商家失敗: ' + err.message);
      navigate('/admin/dashboard');
    } finally {
      setLoadingSeller(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // 清除錯誤
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = '請輸入商家名稱';
    }

    if (!formData.location.trim()) {
      newErrors.location = '請輸入所在地';
    }

    const rating = parseFloat(formData.rating);
    if (isNaN(rating) || rating < 0 || rating > 5) {
      newErrors.rating = '評分必須在 0-5 之間';
    }

    const responseRate = parseInt(formData.response_rate);
    if (isNaN(responseRate) || responseRate < 0 || responseRate > 100) {
      newErrors.response_rate = '回應率必須在 0-100 之間';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const url = isEditMode
        ? `${process.env.REACT_APP_API_BASE_URL}/admin/sellers/${id}`
        : `${process.env.REACT_APP_API_BASE_URL}/admin/sellers`;

      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.error) {
        throw new Error(result.message);
      }

      alert(isEditMode ? '商家更新成功！' : '商家新增成功！');
      navigate('/admin/dashboard');

    } catch (err) {
      console.error('提交失敗:', err);
      alert('提交失敗: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loadingSeller) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isEditMode ? '編輯商家' : '新增商家'}
              </h1>
              <p className="mt-1 text-gray-600">
                {isEditMode ? '更新商家資訊' : '填寫商家詳細資料'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 基本資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-6">基本資訊</h2>

            <div className="space-y-4">
              {/* 商家名稱 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Store size={16} className="inline mr-1" />
                  商家名稱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="請輸入商家名稱，例如：木藝專賣店"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              {/* 所在地 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin size={16} className="inline mr-1" />
                  所在地 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 ${
                    errors.location ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="例如：台北市、台中市"
                />
                {errors.location && (
                  <p className="mt-1 text-sm text-red-500">{errors.location}</p>
                )}
              </div>

              {/* 評分 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  商家評分（0-5）
                </label>
                <input
                  type="number"
                  name="rating"
                  value={formData.rating}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                  max="5"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 ${
                    errors.rating ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.0"
                />
                {errors.rating && (
                  <p className="mt-1 text-sm text-red-500">{errors.rating}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  預設為 0.0，系統會根據商品評價自動更新
                </p>
              </div>

              {/* 回應率和回應時間 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    回應率（%）
                  </label>
                  <input
                    type="number"
                    name="response_rate"
                    value={formData.response_rate}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 ${
                      errors.response_rate ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="95"
                  />
                  {errors.response_rate && (
                    <p className="mt-1 text-sm text-red-500">{errors.response_rate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    回應時間
                  </label>
                  <select
                    name="response_time"
                    value={formData.response_time}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="1小時內">1小時內</option>
                    <option value="4小時內">4小時內</option>
                    <option value="12小時內">12小時內</option>
                    <option value="24小時內">24小時內</option>
                    <option value="48小時內">48小時內</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* 提交按鈕 */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save size={18} />
              {loading ? '處理中...' : (isEditMode ? '更新商家' : '新增商家')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminSellerForm;